import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Calendar, Tag, Users, Clock, DollarSign, Briefcase, ExternalLink, Timer, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { Offer } from '@/store/offers';
import { getOfferCountdown } from '@/utils/offerCountdown';

interface OfferPreviewModalProps {
  open: boolean;
  onClose: () => void;
  offer: Offer | undefined;
  creatorName: string;
  applicantCount: number;
  hasApplied: boolean;
  canApply: boolean;
  onApply: () => void;
  onContactCreator?: () => void;
}

const TYPE_LABELS: Record<string, string> = {
  tryout: 'Tryout',
  recruiting: 'Recruiting',
  camp: 'Camp',
  event: 'Event',
};

const OfferPreviewModal = ({
  open, onClose, offer, creatorName, applicantCount, hasApplied, canApply, onApply, onContactCreator,
}: OfferPreviewModalProps) => {
  const navigate = useNavigate();

  if (!offer) return null;

  const countdown = getOfferCountdown(offer.date);

  const handleApply = () => {
    if (!hasApplied && !countdown.expired) {
      onApply();
    }
  };

  const handleViewFull = () => {
    onClose();
    navigate(`/offers/${offer.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="!w-[calc(100vw-1.5rem)] !max-w-[22rem] overflow-hidden rounded-2xl border-border bg-card p-0 sm:!w-full sm:!max-w-md">
        {/* Header accent */}
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 to-emerald-400" />

        <div className="max-h-[80vh] space-y-4 overflow-x-hidden overflow-y-auto px-3 pb-5 pt-4 sm:px-4">
          <DialogHeader className="space-y-1.5 pr-8">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-[10px] uppercase tracking-wider font-semibold">
                <Briefcase className="mr-1 h-3 w-3" />
                {TYPE_LABELS[offer.type] ?? offer.type}
              </Badge>
              <span className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border',
                countdown.expired
                  ? 'border-border bg-muted text-muted-foreground'
                  : countdown.urgent
                    ? 'border-destructive/30 bg-destructive/10 text-destructive'
                    : 'border-primary/30 bg-primary/10 text-primary'
              )}>
                <Timer className="h-2.5 w-2.5" />
                {countdown.label}
              </span>
            </div>
            <DialogTitle className="text-lg font-bold text-foreground leading-snug">
              {offer.title}
            </DialogTitle>
            <p className="text-xs text-muted-foreground">Posted by {creatorName}</p>
          </DialogHeader>

          {/* Description */}
          <p className="text-sm leading-relaxed text-muted-foreground">{offer.description}</p>

          {/* Meta grid */}
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            <MetaItem icon={<MapPin className="h-3.5 w-3.5" />} label="Location" value={offer.location} />
            <MetaItem icon={<Calendar className="h-3.5 w-3.5" />} label="Date" value={new Date(offer.date).toLocaleDateString()} />
            {offer.level && <MetaItem icon={<Tag className="h-3.5 w-3.5" />} label="Level" value={offer.level} />}
            {offer.time && <MetaItem icon={<Clock className="h-3.5 w-3.5" />} label="Time" value={offer.time} />}
            {offer.price && <MetaItem icon={<DollarSign className="h-3.5 w-3.5" />} label="Price" value={offer.price} />}
            <MetaItem icon={<Users className="h-3.5 w-3.5" />} label="Applicants" value={String(applicantCount)} />
          </div>

          {/* Positions */}
          {offer.positionsWanted && offer.positionsWanted.length > 0 && (
            <div className="space-y-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Positions wanted</span>
              <div className="flex flex-wrap gap-1.5">
                {offer.positionsWanted.map((pos) => (
                  <span key={pos} className="rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs font-medium text-foreground">
                    {pos}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-2 pt-1">
            {canApply && (
              <Button
                onClick={handleApply}
                disabled={hasApplied || countdown.expired}
                className={cn(
                  'w-full rounded-full font-semibold transition-all duration-200 active:scale-[0.97]',
                  countdown.expired
                    ? 'bg-muted text-muted-foreground cursor-default'
                    : hasApplied
                      ? 'bg-primary/20 text-primary cursor-default'
                      : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/20'
                )}
              >
                {countdown.expired ? 'Closed' : hasApplied ? 'Applied ✓' : 'Apply Now'}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleViewFull}
              className="w-full min-w-0 rounded-full border-border gap-1.5"
            >
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              <span>View Full Offer</span>
            </Button>
            {onContactCreator && (
              <Button
                variant="outline"
                onClick={() => { onClose(); onContactCreator(); }}
                className="w-full min-w-0 rounded-full border-border gap-1.5"
              >
                <MessageCircle className="h-3.5 w-3.5 shrink-0" />
                Contact
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MetaItem = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
  <div className="flex items-start gap-2 rounded-lg bg-secondary/60 px-3 py-2">
    <span className="mt-0.5 text-muted-foreground">{icon}</span>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-xs font-medium text-foreground truncate">{value}</p>
    </div>
  </div>
);

export default OfferPreviewModal;
